<template>
  <AIVDialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    title="Intake Form"
    width="95%"
    height="85vh"
    class="intake-form-modal"
  >
    <!-- Client name / status in title slot -->
    <template slot="title">
      Initial Client Intake Form
      <span v-if="form.client_first_name" style="font-weight:400; color:#b0b0c0;">
        - {{ form.client_first_name }} {{ form.client_last_name }}
      </span>
      <el-tag v-if="form.status" size="small" :type="form.status === 'completed' ? 'success' : 'warning'" class="ml-2">
        {{ form.status }}
      </el-tag>
    </template>

    <!-- Step Indicator -->
    <el-steps :active="currentStep" finish-status="success" align-center class="intake-steps">
      <el-step title="Client Info" />
      <el-step title="Guardian" />
      <el-step title="Support Coordinator" />
      <el-step title="Clinical" />
      <el-step title="Supports & Emergency" />
      <el-step title="Physician & Healthcare" />
      <el-step title="Schedule" />
      <el-step title="Health & Safety" />
      <el-step title="Seizure / Emergency" />
      <el-step title="Medications" />
      <el-step title="Review" />
    </el-steps>

    <div class="intake-form__body" v-if="form.id">
      <!-- Step 1: Client Information -->
      <div v-show="currentStep === 0">
        <h5 class="step-title">Client Information</h5>
        <el-form ref="formStep0" :model="form" :rules="rules" label-position="top">
          <div class="form-grid">
            <el-form-item label="Client Full Legal Name - First" prop="client_first_name">
              <el-input v-model="form.client_first_name" />
            </el-form-item>
            <el-form-item label="Client Full Legal Name - Last" prop="client_last_name">
              <el-input v-model="form.client_last_name" />
            </el-form-item>
            <el-form-item label="Nickname or Preferred Name">
              <el-input v-model="form.goes_by" placeholder="None" />
            </el-form-item>
            <el-form-item label="Date of Birth" prop="dob">
              <el-date-picker v-model="form.dob" type="date" value-format="yyyy-MM-dd" style="width:100%" />
            </el-form-item>
          </div>
          <el-form-item label="Address">
            <el-input v-model="form.address_line1" placeholder="Street address" />
          </el-form-item>
          <el-form-item label="Address Line 2">
            <el-input v-model="form.address_line2" placeholder="Gate code, parking instructions, etc." />
          </el-form-item>
          <div class="form-grid form-grid--3">
            <el-form-item label="City">
              <el-input v-model="form.city" />
            </el-form-item>
            <el-form-item label="State">
              <el-input v-model="form.state" />
            </el-form-item>
            <el-form-item label="Zip">
              <el-input v-model="form.zip" />
            </el-form-item>
          </div>
          <div class="form-grid">
            <el-form-item label="Email Address">
              <el-input v-model="form.client_email" />
            </el-form-item>
            <el-form-item label="Phone Number" prop="primary_phone">
              <el-input v-model="form.primary_phone" />
            </el-form-item>
          </div>
        </el-form>
      </div>

      <!-- Step 2: Guardian & Support Coordinator -->
      <div v-show="currentStep === 1">
        <h5 class="step-title">Guardian / Legal Representative Contact Information</h5>
        <el-form ref="formStep1Guardian" :model="form" :rules="rules" label-position="top">
          <div class="form-grid">
            <el-form-item label="Name" prop="guardian_name">
              <el-input v-model="form.guardian_name" />
            </el-form-item>
            <el-form-item label="Relationship">
              <el-input v-model="form.guardian_relationship" />
            </el-form-item>
          </div>
          <el-form-item class="address-label-item">
            <template slot="label">
              Address
              <el-checkbox v-model="sameAsClientAddress" style="font-weight: normal; margin-left: 12px;">Same as client</el-checkbox>
            </template>
            <el-input
              v-model="form.guardian_address"
              type="textarea"
              :rows="2"
              :disabled="sameAsClientAddress"
            />
          </el-form-item>
          <el-form-item label="Mailing Address">
            <el-input v-model="form.guardian_mailing_address" type="textarea" :rows="2" placeholder="If different from above" />
          </el-form-item>
          <div class="form-grid">
            <el-form-item label="Email" prop="guardian_email">
              <el-input v-model="form.guardian_email" />
            </el-form-item>
            <el-form-item label="Website">
              <el-input v-model="form.guardian_website" placeholder="N/A" />
            </el-form-item>
            <el-form-item label="Phone" prop="guardian_phone">
              <el-input v-model="form.guardian_phone" />
            </el-form-item>
            <el-form-item label="Fax">
              <el-input v-model="form.guardian_fax" placeholder="N/A" />
            </el-form-item>
          </div>
        </el-form>
      </div>

      <!-- Step 3: Support Coordinator -->
      <div v-show="currentStep === 2">
        <h5 class="step-title">Support Coordinator Contact Information</h5>
        <el-form ref="formStep2SC" :model="form" :rules="rules" label-position="top">
          <div class="form-grid">
            <el-form-item label="Name" prop="support_coordinator_name">
              <el-input v-model="form.support_coordinator_name" />
            </el-form-item>
            <el-form-item label="Email" prop="support_coordinator_email">
              <el-input v-model="form.support_coordinator_email" />
            </el-form-item>
            <el-form-item label="Website">
              <el-input v-model="form.support_coordinator_website" placeholder="N/A" />
            </el-form-item>
            <el-form-item label="Phone" prop="support_coordinator_phone">
              <el-input v-model="form.support_coordinator_phone" />
            </el-form-item>
            <el-form-item label="Fax">
              <el-input v-model="form.support_coordinator_fax" placeholder="N/A" />
            </el-form-item>
          </div>
        </el-form>
      </div>

      <!-- Step 4: Clinical (Client Information page 2) -->
      <div v-show="currentStep === 3">
        <h5 class="step-title">Client Information</h5>
        <el-form :model="form" label-position="top">
          <el-form-item label="Diagnosis">
            <el-input v-model="diagnosesText" type="textarea" :rows="2" placeholder="Enter diagnoses separated by commas" />
          </el-form-item>
          <el-form-item label="Likes">
            <el-input v-model="form.likes" type="textarea" :rows="4" />
          </el-form-item>
          <el-form-item label="Dislikes">
            <el-input v-model="form.dislikes" type="textarea" :rows="4" />
          </el-form-item>
          <el-form-item label="Challenges">
            <el-input v-model="form.challenges" type="textarea" :rows="4" />
          </el-form-item>
          <el-form-item label="Obstacles">
            <el-input v-model="form.obstacles" type="textarea" :rows="3" />
          </el-form-item>
          <el-form-item label="Goals">
            <el-input v-model="form.goals" type="textarea" :rows="4" />
          </el-form-item>
        </el-form>
      </div>

      <!-- Step 5: Supports & Contacts -->
      <div v-show="currentStep === 4">
        <!-- Other Important Supports -->
        <h5 class="step-title">
          Other Important Supports Contact Information
          <el-button type="primary" size="mini" @click="addContact('other_support')">
            <i class="fa fa-plus"></i> Add
          </el-button>
        </h5>
        <AIVTable :columns="supportColumns" :data="otherSupportContacts" :modal-table="true">
          <template #cell-title="{ row }">
            <el-input v-model="row.title" size="mini" placeholder="e.g. Sister, Brother" />
          </template>
          <template #cell-name="{ row }">
            <el-input v-model="row.name" size="mini" />
          </template>
          <template #cell-phone="{ row }">
            <el-input v-model="row.phone" size="mini" />
          </template>
          <template #cell-email="{ row }">
            <el-input v-model="row.email" size="mini" />
          </template>
          <template #cell-actions="{ row }">
            <button class="btn btn-sm btn-danger" @click="removeContact(row)" title="Remove">
              <i class="fa fa-trash"></i>
            </button>
          </template>
        </AIVTable>

        <!-- Emergency Contact -->
        <h5 class="step-title mt-4">Emergency Contact Information</h5>
        <el-form :model="form" label-position="top">
          <div v-for="(contact, idx) in emergencyContacts" :key="'ec-' + idx">
            <div class="form-grid">
              <el-form-item label="Name">
                <el-input v-model="contact.name" />
              </el-form-item>
              <el-form-item label="Physical Address">
                <el-input v-model="contact.address" />
              </el-form-item>
              <el-form-item label="Phone">
                <el-input v-model="contact.phone" />
              </el-form-item>
              <el-form-item label="Best Mode of Contact">
                <el-input v-model="contact.notes" placeholder="e.g. text, call" />
              </el-form-item>
              <el-form-item label="Alt. Phone">
                <el-input v-model="contact.phone_alt" />
              </el-form-item>
            </div>
          </div>
          <el-button v-if="emergencyContacts.length === 0" size="mini" @click="addContact('emergency')">
            <i class="fa fa-plus"></i> Add Emergency Contact
          </el-button>
        </el-form>

      </div>

      <!-- Step 6: Physician & Healthcare Contacts -->
      <div v-show="currentStep === 5">
        <h5 class="step-title">Primary Care Physician / Health Care Professional Information</h5>
        <el-form :model="form" label-position="top">
          <div v-for="(contact, idx) in physicianContacts" :key="'ph-' + idx">
            <div class="form-grid">
              <el-form-item label="Name">
                <el-input v-model="contact.name" />
              </el-form-item>
              <el-form-item label="Facility Name">
                <el-input v-model="contact.facility_name" />
              </el-form-item>
              <el-form-item label="Address">
                <el-input v-model="contact.address" />
              </el-form-item>
              <el-form-item label="Email">
                <el-input v-model="contact.email" />
              </el-form-item>
              <el-form-item label="Website">
                <el-input v-model="contact.website" />
              </el-form-item>
              <el-form-item label="Phone">
                <el-input v-model="contact.phone" />
              </el-form-item>
              <el-form-item label="Fax">
                <el-input v-model="contact.fax" />
              </el-form-item>
            </div>
          </div>
          <el-button v-if="physicianContacts.length === 0" size="mini" @click="addContact('physician')">
            <i class="fa fa-plus"></i> Add Physician
          </el-button>
        </el-form>

        <!-- Other Health Care Contacts -->
        <h5 class="step-title mt-4">
          Other Important Health Care Contacts
          <small class="text-muted">(Mental Health, Chiropractor, Behaviorist, etc.)</small>
          <el-button type="primary" size="mini" @click="addContact('healthcare')">
            <i class="fa fa-plus"></i> Add
          </el-button>
        </h5>
        <AIVTable :columns="healthcareColumns" :data="healthcareContacts" :modal-table="true">
          <template #cell-title="{ row }">
            <el-input v-model="row.title" size="mini" placeholder="e.g. Mental Health, Behaviorist" />
          </template>
          <template #cell-name="{ row }">
            <el-input v-model="row.name" size="mini" />
          </template>
          <template #cell-phone="{ row }">
            <el-input v-model="row.phone" size="mini" />
          </template>
          <template #cell-email="{ row }">
            <el-input v-model="row.email" size="mini" />
          </template>
          <template #cell-actions="{ row }">
            <button class="btn btn-sm btn-danger" @click="removeContact(row)" title="Remove">
              <i class="fa fa-trash"></i>
            </button>
          </template>
        </AIVTable>
      </div>

      <!-- Step 7: Schedule of Services -->
      <div v-show="currentStep === 6">
        <h5 class="step-title">Schedule of Services</h5>
        <p class="text-muted small">Please tell us your schedule including school, therapy, other services that may affect your service and schedule with OPPO.</p>
        <div class="schedule-table-wrapper">
          <table class="table table-bordered schedule-table">
            <thead>
              <tr>
                <th>Type of Support</th>
                <th v-for="day in weekdays" :key="day">{{ day }}</th>
                <th>Total Hours</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="type in serviceTypes" :key="type">
                <td class="schedule-type-label">{{ type }}</td>
                <td v-for="day in weekdays" :key="type + '-' + day">
                  <el-input
                    v-model="scheduleGrid[type][day]"
                    size="mini"
                    placeholder="-"
                  />
                </td>
                <td>
                  <el-input
                    v-model="scheduleGrid[type].totalHours"
                    size="mini"
                    placeholder="-"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Step 8: Health & Safety -->
      <div v-show="currentStep === 7">
        <h5 class="step-title">Health & Safety Information</h5>
        <el-form ref="formStep5" :model="form" :rules="rules" label-position="top">
          <el-form-item label="Emergency and Fire Evacuation" prop="emergency_protocol">
            <el-input v-model="form.emergency_protocol" type="textarea" :rows="3" />
          </el-form-item>
          <el-form-item label="Health Aides (Glasses, hearing aide, wheelchair, etc.)">
            <el-input v-model="form.health_aides" type="textarea" :rows="2" />
          </el-form-item>
          <el-form-item label="Personal Care (Hygiene, etc.)">
            <el-input v-model="form.personal_care" type="textarea" :rows="2" />
          </el-form-item>
          <el-form-item label="Supervision (Street Safety, Unsupervised time, Sexual Concerns, etc.)">
            <el-input v-model="form.supervision_notes" type="textarea" :rows="3" />
          </el-form-item>
          <el-form-item label="Lifting and Transportation (if applicable)">
            <el-input v-model="form.lifting_transportation" type="textarea" :rows="2" />
          </el-form-item>
          <el-form-item label="Medication Administration">
            <el-select v-model="form.medication_admin_during_services" style="width:100%">
              <el-option label="Yes" value="yes" />
              <el-option label="No" value="no" />
              <el-option label="None" value="N/A" />
            </el-select>
          </el-form-item>
          <el-form-item label="Eating/Dietary Concerns or Suggestions">
            <el-input v-model="form.dietary_needs" type="textarea" :rows="2" />
          </el-form-item>
          <el-form-item label="Allergies">
            <el-input v-model="form.allergies" type="textarea" :rows="2" />
          </el-form-item>
          <el-form-item label="Known Behaviors (does this individual have a BSP?)">
            <el-input v-model="form.known_behaviors" type="textarea" :rows="3" />
          </el-form-item>
        </el-form>
      </div>

      <!-- Step 9: Emergency Protocol and Seizure Information -->
      <div v-show="currentStep === 8">
        <h5 class="step-title">Emergency Protocol and Seizure Information</h5>
        <el-form :model="form" label-position="top">
          <el-form-item label="Describe what a seizure or other specific or recurrent emergency/episode would look like for yourself or the individual whom you have guardianship over below:">
            <el-input v-model="seizureDescription" type="textarea" :rows="4" />
          </el-form-item>
          <el-form-item label="Describe the appropriate protocol for OPPO's staff to follow if the above occurrence happens:">
            <el-input v-model="seizureProtocolResponse" type="textarea" :rows="4" />
          </el-form-item>
        </el-form>
      </div>

      <!-- Step 10: Medication Information -->
      <div v-show="currentStep === 9">
        <h5 class="step-title">Medication Information</h5>
        <el-form :model="form" label-position="top">
          <h6 class="mt-2 mb-3">Prescription and Administration Information</h6>
          <div class="form-grid">
            <el-form-item label="Physician Name">
              <el-input v-model="form.medication_physician_name" />
            </el-form-item>
            <el-form-item label="Contact Info">
              <el-input v-model="form.medication_physician_contact" />
            </el-form-item>
          </div>
          <div class="form-grid">
            <el-form-item label="Will any medications be administered while in OPPO Services?">
              <el-select v-model="form.medication_admin_during_services" style="width:100%">
                <el-option label="Yes" value="yes" />
                <el-option label="No" value="no" />
                <el-option label="N/A" value="N/A" />
              </el-select>
            </el-form-item>
            <el-form-item label="Physician or family member responsible to train staff">
              <el-input v-model="form.medication_training_responsible" />
            </el-form-item>
          </div>
        </el-form>

        <h6 class="mt-4 mb-2">
          Medications
          <el-button type="primary" size="mini" @click="addMedication" class="ml-2">
            <i class="fa fa-plus"></i> Add Medication
          </el-button>
        </h6>
        <AIVTable :columns="medicationColumns" :data="form.medications || []" :modal-table="true">
          <template #cell-name="{ row }">
            <el-input v-model="row.name" size="mini" placeholder="Med Name" />
          </template>
          <template #cell-reason="{ row }">
            <el-input v-model="row.reason" size="mini" />
          </template>
          <template #cell-start_date="{ row }">
            <el-input v-model="row.start_date" size="mini" placeholder="MM/DD/YYYY" />
          </template>
          <template #cell-possible_side_effects="{ row }">
            <el-input v-model="row.possible_side_effects" size="mini" />
          </template>
          <template #cell-actions="{ row }">
            <button class="btn btn-sm btn-danger" @click="removeMedication(form.medications.indexOf(row))" title="Remove">
              <i class="fa fa-trash"></i>
            </button>
          </template>
        </AIVTable>
      </div>

      <!-- Step 11: Review & Complete -->
      <div v-show="currentStep === 10">
        <h5 class="step-title">Review & Complete</h5>
        <div class="review-section">
          <div class="review-block">
            <h6>Client Information</h6>
            <p><strong>Name:</strong> {{ form.client_first_name }} {{ form.client_last_name }}</p>
            <p v-if="form.goes_by"><strong>Nickname:</strong> {{ form.goes_by }}</p>
            <p><strong>DOB:</strong> {{ form.dob }}</p>
            <p><strong>Phone:</strong> {{ form.primary_phone }}</p>
            <p><strong>Email:</strong> {{ form.client_email || 'N/A' }}</p>
            <p><strong>Address:</strong> {{ form.address_line1 }} {{ form.city }}, {{ form.state }} {{ form.zip }}</p>
          </div>
          <div class="review-block">
            <h6>Guardian / Legal Representative</h6>
            <p><strong>Name:</strong> {{ form.guardian_name || 'N/A' }}</p>
            <p><strong>Phone:</strong> {{ form.guardian_phone || 'N/A' }}</p>
            <p><strong>Email:</strong> {{ form.guardian_email || 'N/A' }}</p>
          </div>
          <div class="review-block">
            <h6>Support Coordinator</h6>
            <p><strong>Name:</strong> {{ form.support_coordinator_name || 'N/A' }}</p>
            <p><strong>Phone:</strong> {{ form.support_coordinator_phone || 'N/A' }}</p>
          </div>
          <div class="review-block">
            <h6>Contacts</h6>
            <p>{{ (form.contacts || []).length }} contact(s) added</p>
          </div>
          <div class="review-block">
            <h6>Health & Safety</h6>
            <p><strong>Emergency/Fire Evacuation:</strong> {{ form.emergency_protocol || 'N/A' }}</p>
            <p><strong>Health Aides:</strong> {{ form.health_aides || 'N/A' }}</p>
            <p><strong>Personal Care:</strong> {{ form.personal_care || 'N/A' }}</p>
            <p><strong>Supervision:</strong> {{ form.supervision_notes || 'N/A' }}</p>
            <p><strong>Lifting/Transportation:</strong> {{ form.lifting_transportation || 'N/A' }}</p>
            <p><strong>Medication Administration:</strong> {{ form.medication_admin_during_services || 'N/A' }}</p>
            <p><strong>Eating/Dietary:</strong> {{ form.dietary_needs || 'N/A' }}</p>
            <p><strong>Allergies:</strong> {{ form.allergies || 'N/A' }}</p>
            <p><strong>Known Behaviors:</strong> {{ form.known_behaviors || 'N/A' }}</p>
          </div>
          <div class="review-block">
            <h6>Medications</h6>
            <p>{{ (form.medications || []).length }} medication(s) added</p>
            <p><strong>Meds administered during services:</strong> {{ form.medication_admin_during_services || 'N/A' }}</p>
          </div>
          <div class="review-block">
            <h6>Intake Details</h6>
            <el-form :model="form" label-position="top">
              <div class="form-grid">
                <el-form-item label="Intake Prepared By (name)">
                  <el-input v-model="form.prepared_by" />
                </el-form-item>
                <el-form-item label="Date of Intake">
                  <el-date-picker v-model="form.intake_date" type="date" value-format="yyyy-MM-dd" style="width:100%" />
                </el-form-item>
              </div>
            </el-form>
          </div>
        </div>

        <div class="text-center mt-4" v-if="form.status === 'draft'">
          <el-button type="success" size="medium" @click="completeIntake">
            <i class="fa fa-check"></i> Complete Intake
          </el-button>
        </div>
        <el-alert
          v-if="form.status === 'completed'"
          title="This intake form has been completed."
          type="success"
          show-icon
          :closable="false"
          class="mt-3"
        />
      </div>
    </div>

    <template slot="footer">
      <el-button v-if="currentStep > 0" @click="prevStep">
        <i class="fa fa-arrow-left"></i> Previous
      </el-button>
      <el-button v-if="currentStep < 10" type="primary" @click="nextStep">
        Next <i class="fa fa-arrow-right"></i>
      </el-button>
      <el-button v-if="form.status === 'draft'" type="info" @click="saveDraft">
        <i class="fa fa-save"></i> Save Draft
      </el-button>
    </template>
  </AIVDialog>
</template>

<script>
import { intakeFormsService } from "@/serviceHandlers/intakeFormsService";

export default {
  name: "IntakeFormModal",
  props: {
    visible: { type: Boolean, default: false },
    formId: { type: [Number, String], default: null },
    client: { type: Object, default: null },
  },
  data() {
    return {
      form: {},
      currentStep: 0,
      weekdays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      serviceTypes: ["DSG", "DSP", "MTP"],
      scheduleGrid: {
        DSG: { Monday: "", Tuesday: "", Wednesday: "", Thursday: "", Friday: "", Saturday: "", Sunday: "", totalHours: "" },
        DSP: { Monday: "", Tuesday: "", Wednesday: "", Thursday: "", Friday: "", Saturday: "", Sunday: "", totalHours: "" },
        MTP: { Monday: "", Tuesday: "", Wednesday: "", Thursday: "", Friday: "", Saturday: "", Sunday: "", totalHours: "" },
      },
      sameAsClientAddress: false,
      diagnosesText: "",
      seizureDescription: "",
      seizureProtocolResponse: "",
      supportColumns: [
        { key: "title",   label: "Title",           sortable: false },
        { key: "name",    label: "Name & Address",  sortable: false },
        { key: "phone",   label: "Phone",           sortable: false },
        { key: "email",   label: "Email",           sortable: false },
        { key: "actions", label: "",                 sortable: false },
      ],
      healthcareColumns: [
        { key: "title",   label: "Title",   sortable: false },
        { key: "name",    label: "Name",    sortable: false },
        { key: "phone",   label: "Phone",   sortable: false },
        { key: "email",   label: "Email",   sortable: false },
        { key: "actions", label: "",         sortable: false },
      ],
      medicationColumns: [
        { key: "name",                  label: "Med Name",             sortable: false },
        { key: "reason",                label: "Reason",               sortable: false },
        { key: "start_date",            label: "Med Start Date",       sortable: false },
        { key: "possible_side_effects", label: "Possible Side Effects", sortable: false },
        { key: "actions",               label: "",                      sortable: false },
      ],
      rules: {
        client_first_name: [{ required: true, message: "Required", trigger: "blur" }],
        client_last_name:  [{ required: true, message: "Required", trigger: "blur" }],
        dob:               [{ required: true, message: "Required", trigger: "change" }],
        primary_phone:     [{ required: true, message: "Required", trigger: "blur" }],
        guardian_name:     [{ required: true, message: "Required", trigger: "blur" }],
        guardian_phone:    [{ required: true, message: "Required", trigger: "blur" }],
        guardian_email:    [{ required: true, message: "Required", trigger: "blur" }],
        support_coordinator_name:  [{ required: true, message: "Required", trigger: "blur" }],
        support_coordinator_phone: [{ required: true, message: "Required", trigger: "blur" }],
        support_coordinator_email: [{ required: true, message: "Required", trigger: "blur" }],
        emergency_protocol:[{ required: true, message: "Required", trigger: "blur" }],
      },
    };
  },
  computed: {
    clientAddressString() {
      const parts = [
        this.form.address_line1,
        this.form.address_line2,
        this.form.city && this.form.state
          ? `${this.form.city}, ${this.form.state} ${this.form.zip || ""}`.trim()
          : (this.form.city || this.form.state || this.form.zip),
      ].filter(Boolean);
      return parts.join("\n");
    },
    otherSupportContacts() {
      return (this.form.contacts || []).filter((c) => c.contact_type === "other_support");
    },
    emergencyContacts() {
      return (this.form.contacts || []).filter((c) => c.contact_type === "emergency");
    },
    physicianContacts() {
      return (this.form.contacts || []).filter((c) => c.contact_type === "physician");
    },
    healthcareContacts() {
      return (this.form.contacts || []).filter(
        (c) => c.contact_type === "healthcare" || c.contact_type === "mental_health" || c.contact_type === "chiropractor" || c.contact_type === "behaviorist"
      );
    },
  },
  watch: {
    formId(id) {
      if (id) this.loadForm(id);
    },
    diagnosesText(val) {
      this.form.diagnoses = val.split(",").map((s) => s.trim()).filter(Boolean);
    },
    sameAsClientAddress(val) {
      if (val) {
        this.form.guardian_address = this.clientAddressString;
      }
    },
    clientAddressString(val) {
      if (this.sameAsClientAddress) {
        this.form.guardian_address = val;
      }
    },
    visible(val) {
      if (val && this.formId) {
        this.loadForm(this.formId);
      }
      if (!val) {
        this.currentStep = 0;
        this.form = {};
        this.resetScheduleGrid();
        this.sameAsClientAddress = false;
        this.seizureDescription = "";
        this.seizureProtocolResponse = "";
      }
    },
  },
  methods: {
    resetScheduleGrid() {
      for (const type of this.serviceTypes) {
        for (const day of this.weekdays) {
          this.scheduleGrid[type][day] = "";
        }
        this.scheduleGrid[type].totalHours = "";
      }
    },
    async loadForm(id) {
      this.$spinner.show();
      try {
        this.form = await intakeFormsService.getById(id);
        let diag = this.form.diagnoses;
        if (typeof diag === "string") {
          try { diag = JSON.parse(diag); } catch { /* keep as string */ }
        }
        this.diagnosesText = Array.isArray(diag)
          ? diag.join(", ")
          : (diag || "");

        // Load schedule grid from saved JSON
        this.resetScheduleGrid();
        if (this.form.schedule_of_services) {
          let saved = this.form.schedule_of_services;
          if (typeof saved === "string") {
            try { saved = JSON.parse(saved); } catch { saved = null; }
          }
          if (saved) {
            for (const type of this.serviceTypes) {
              if (saved[type]) {
                for (const day of this.weekdays) {
                  if (saved[type][day] !== undefined) {
                    this.$set(this.scheduleGrid[type], day, saved[type][day]);
                  }
                }
              }
            }
          }
        }

        // Parse seizure_protocol into two parts
        if (this.form.seizure_protocol && this.form.seizure_protocol !== "N/A") {
          const parts = this.form.seizure_protocol.split("\n\n");
          this.seizureDescription = parts[0] || "";
          this.seizureProtocolResponse = parts.slice(1).join("\n\n") || "";
        }

        this.prefillFromClient();
      } catch (error) {
        this.$notifyError("Could not load intake form: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    prefillFromClient() {
      if (!this.client) return;
      const addr = this.client.addresses?.[0] || this.client.address || {};
      const fieldMap = {
        client_first_name: this.client.first_name,
        client_last_name:  this.client.last_name,
        goes_by:           this.client.goes_by,
        dob:               this.client.dob,
        primary_phone:     this.client.best_contact_phone,
        secondary_phone:   this.client.secondary_phone,
        client_email:      this.client.client_email || this.client.best_contact_email,
        address_line1:     addr.address1,
        address_line2:     addr.address2,
        city:              addr.city,
        state:             addr.state,
        zip:               addr.zip,
        medicaid_number:   this.client.medicaid_number,
        dspd_pid:          this.client.dspd_pid,
      };
      for (const [formField, clientValue] of Object.entries(fieldMap)) {
        if (!this.form[formField] && clientValue) {
          this.$set(this.form, formField, clientValue);
        }
      }
    },
    async nextStep() {
      if (this.currentStep >= 10) return;
      const valid = await this.validateCurrentStep();
      if (!valid) return;
      await this.saveDraft();
      this.currentStep++;
      this.$nextTick(() => {
        const body = this.$el.querySelector('.intake-form__body');
        if (body) body.scrollTop = 0;
      });
    },
    validateCurrentStep() {
      const refMap = {
        0: ["formStep0"],
        1: ["formStep1Guardian"],
        2: ["formStep2SC"],
        7: ["formStep5"],
      };
      const refs = refMap[this.currentStep];
      if (!refs) return Promise.resolve(true);
      return Promise.all(
        refs.map((ref) => new Promise((resolve) => this.$refs[ref].validate((v) => resolve(v))))
      ).then((results) => results.every(Boolean));
    },
    validateAll() {
      const allRefs = ["formStep0", "formStep1Guardian", "formStep2SC", "formStep5"];
      return Promise.all(
        allRefs.map((ref) => new Promise((resolve) => this.$refs[ref].validate((v) => resolve(v))))
      ).then((results) => results.every(Boolean));
    },
    prevStep() {
      if (this.currentStep <= 0) return;
      this.currentStep--;
      this.$nextTick(() => {
        const body = this.$el.querySelector('.intake-form__body');
        if (body) body.scrollTop = 0;
      });
    },
    buildSchedulePayload() {
      const schedule = {};
      for (const type of this.serviceTypes) {
        const dayData = {};
        let hasValue = false;
        for (const day of this.weekdays) {
          const val = this.scheduleGrid[type][day];
          if (val && val.trim()) {
            dayData[day] = val.trim();
            hasValue = true;
          }
        }
        if (hasValue) {
          schedule[type] = dayData;
        }
      }
      return Object.keys(schedule).length > 0 ? schedule : null;
    },
    buildSeizureProtocol() {
      const parts = [this.seizureDescription, this.seizureProtocolResponse].filter(Boolean);
      return parts.length > 0 ? parts.join("\n\n") : "N/A";
    },
    buildFormPayload() {
      const f = this.form;
      return {
        // Client Info
        client_first_name: f.client_first_name,
        client_last_name:  f.client_last_name,
        goes_by:           f.goes_by,
        dob:               f.dob,
        gender:            f.gender,
        height:            f.height,
        weight:            f.weight,
        primary_phone:     f.primary_phone,
        secondary_phone:   f.secondary_phone,
        client_email:      f.client_email,
        address_line1:     f.address_line1,
        address_line2:     f.address_line2,
        city:              f.city,
        state:             f.state,
        zip:               f.zip,
        medicaid_number:   f.medicaid_number,
        dspd_pid:          f.dspd_pid,
        // Guardian
        guardian_name:            f.guardian_name,
        guardian_relationship:    f.guardian_relationship,
        guardian_phone:           f.guardian_phone,
        guardian_email:           f.guardian_email,
        guardian_address:         f.guardian_address,
        guardian_mailing_address: f.guardian_mailing_address,
        guardian_fax:             f.guardian_fax,
        guardian_website:         f.guardian_website,
        // Support Coordinator
        support_coordinator_name:    f.support_coordinator_name,
        support_coordinator_phone:   f.support_coordinator_phone,
        support_coordinator_email:   f.support_coordinator_email,
        support_coordinator_fax:     f.support_coordinator_fax,
        support_coordinator_website: f.support_coordinator_website,
        // Clinical
        diagnoses:  f.diagnoses,
        likes:      f.likes,
        dislikes:   f.dislikes,
        challenges: f.challenges,
        obstacles:  f.obstacles,
        goals:      f.goals,
        // Health & Safety
        emergency_protocol:     f.emergency_protocol,
        health_aides:           f.health_aides,
        personal_care:          f.personal_care,
        supervision_notes:      f.supervision_notes,
        lifting_transportation: f.lifting_transportation,
        dietary_needs:          f.dietary_needs,
        allergies:              f.allergies,
        known_behaviors:        f.known_behaviors,
        // Seizure
        seizure_protocol: this.buildSeizureProtocol(),
        // Schedule
        schedule_of_services: this.buildSchedulePayload(),
        // Medication
        medication_admin_during_services: f.medication_admin_during_services,
        medication_training_responsible:  f.medication_training_responsible,
        medication_physician_name:        f.medication_physician_name,
        medication_physician_contact:     f.medication_physician_contact,
        // Intake details
        prepared_by: f.prepared_by,
        intake_date: f.intake_date,
        // Legacy fields (keep for backward compat)
        functional_limitations: f.functional_limitations,
        medical_needs:          f.medical_needs,
        bathroom_needs:         f.bathroom_needs,
        transportation_needs:   f.transportation_needs,
        dnr:                    f.dnr,
        // Status
        status: f.status,
      };
    },
    async saveDraft() {
      if (this.form.status !== "draft") return;
      try {
        await this.saveContactsAndMedications();
        const formData = this.buildFormPayload();
        await intakeFormsService.update(this.form.id, formData);
        this.$notifySuccess("Draft saved successfully.");
      } catch (error) {
        this.$notifyError("Could not save draft: " + error);
      }
    },
    addContact(type) {
      if (!this.form.contacts) this.$set(this.form, "contacts", []);
      this.form.contacts.push({
        contact_type: type,
        name: "",
        title: "",
        relationship: "",
        phone: "",
        phone_alt: "",
        email: "",
        fax: "",
        website: "",
        facility_name: "",
        address: "",
        notes: "",
      });
    },
    async removeContact(row) {
      const idx = this.form.contacts.indexOf(row);
      if (idx < 0) return;
      if (row.id) {
        await intakeFormsService.deleteContact(this.form.id, row.id);
      }
      this.form.contacts.splice(idx, 1);
    },
    addMedication() {
      if (!this.form.medications) this.$set(this.form, "medications", []);
      this.form.medications.push({
        name: "",
        reason: "",
        start_date: "",
        possible_side_effects: "",
      });
    },
    async removeMedication(index) {
      const med = this.form.medications[index];
      if (med.id) {
        await intakeFormsService.deleteMedication(this.form.id, med.id);
      }
      this.form.medications.splice(index, 1);
    },
    formatContactType(t) {
      return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    async saveContactsAndMedications() {
      // Contacts
      if (this.form.contacts) {
        for (const c of this.form.contacts) {
          if (c.id) {
            await intakeFormsService.updateContact(this.form.id, c.id, c);
          } else if (c.name && c.name.trim()) {
            const created = await intakeFormsService.createContact(this.form.id, c);
            c.id = created.id;
          }
        }
      }
      // Medications
      if (this.form.medications) {
        for (const m of this.form.medications) {
          if (m.id) {
            await intakeFormsService.updateMedication(this.form.id, m.id, m);
          } else if (m.name && m.name.trim()) {
            const created = await intakeFormsService.createMedication(this.form.id, m);
            m.id = created.id;
          }
        }
      }
    },
    async completeIntake() {
      const valid = await this.validateAll();
      if (!valid) {
        this.$notifyError("Please fill in all required fields before completing.");
        return;
      }
      try {
        this.$spinner.show();
        await this.saveContactsAndMedications();
        const formData = this.buildFormPayload();
        formData.status = "completed";
        await intakeFormsService.update(this.form.id, formData);
        this.form.status = "completed";
        this.$notifySuccess("Intake form completed successfully!");
        this.$emit("completed", this.form);
      } catch (error) {
        this.$notifyError("Could not complete intake: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
  },
};
</script>

<style scoped>
.intake-form-modal .intake-steps {
  padding: 15px 0;
}
/* Active step: blue text and border so it's visible on dark background */
.intake-form-modal .intake-steps :deep(.el-step__head.is-process) {
  color: #409eff;
  border-color: #409eff;
}
.intake-form-modal .intake-steps :deep(.el-step__title.is-process) {
  color: #409eff;
}
.intake-form-modal .intake-steps :deep(.el-step__description.is-process) {
  color: #409eff;
}
.intake-form__body {
  padding: 0 15px 15px;
  overflow-y: auto;
  max-height: calc(85vh - 180px);
}
.step-title {
  color: #fff;
  font-weight: 600;
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 1px solid #444;
}
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0 16px;
}
.form-grid--3 {
  grid-template-columns: 1fr 1fr 1fr;
}
.review-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}
.review-block {
  border: 1px solid #555;
  border-radius: 6px;
  padding: 12px 15px;
}
.review-block h6 {
  margin-bottom: 8px;
  font-weight: 600;
}
.review-block p {
  margin: 4px 0;
  font-size: 0.9em;
}

/* Schedule grid matching document's table layout */
.schedule-table-wrapper {
  overflow-x: auto;
}
.schedule-table th {
  text-align: center;
  font-size: 0.85em;
  padding: 6px 4px;
}
.schedule-table td {
  padding: 4px;
  vertical-align: middle;
}
.schedule-type-label {
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  padding: 6px 10px !important;
}

.address-label-item .el-checkbox {
  font-weight: normal;
}

/* White labels, tighter spacing — match ShiftModal / global dark theme */
.intake-form-modal :deep(.el-form-item__label) {
  color: #ffffff;
  font-weight: 500;
  padding-bottom: 2px;
  line-height: 1.4;
}
.intake-form-modal :deep(.el-form-item) {
  margin-bottom: 14px;
}
</style>
